import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { ILogService } from 'vscode/vscode/vs/platform/log/common/log.service';
import { AbstractLifecycleService } from 'vscode/vscode/vs/workbench/services/lifecycle/common/lifecycleService';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { addDisposableListener, EventType } from 'vscode/vscode/vs/base/browser/dom';
import { WillSaveStateReason } from 'vscode/vscode/vs/platform/storage/common/storage';
import { IStorageService } from 'vscode/vscode/vs/platform/storage/common/storage.service';
import { CancellationToken } from 'vscode/vscode/vs/base/common/cancellation';
import { mainWindow } from 'vscode/vscode/vs/base/browser/window';
import { firstOrDefault } from 'vscode/vscode/vs/base/common/arrays';

const _moduleId = "vs/workbench/services/lifecycle/browser/lifecycleService";
let BrowserLifecycleService = class BrowserLifecycleService extends AbstractLifecycleService {
    constructor(logService, storageService) {
        super(logService, storageService);
        this.beforeUnloadListener = undefined;
        this.unloadListener = undefined;
        this.ignoreBeforeUnload = false;
        this.didUnload = false;
        this.registerListeners();
    }
    registerListeners() {
        this.beforeUnloadListener = addDisposableListener(mainWindow, EventType.BEFORE_UNLOAD, (e) => this.onBeforeUnload(e));
        this.unloadListener = addDisposableListener(mainWindow, EventType.PAGE_HIDE, () => this.onUnload());
    }
    onBeforeUnload(event) {
        if (this.ignoreBeforeUnload) {
            this.logService.info('[lifecycle] onBeforeUnload triggered but ignored once');
            this.ignoreBeforeUnload = false;
        }
        else {
            this.logService.info('[lifecycle] onBeforeUnload triggered and handled with veto support');
            this.doShutdown(() => this.vetoBeforeUnload(event));
        }
    }
    vetoBeforeUnload(event) {
        event.preventDefault();
        event.returnValue = ( localizeWithPath(
            _moduleId,
            0,
            "Changes that you made may not be saved. Please check press 'Cancel' and try again."
        ));
    }
    withExpectedShutdown(reason, callback) {
        if (typeof reason === 'number') {
            this.shutdownReason = reason;
            return this.storageService.flush(WillSaveStateReason.SHUTDOWN);
        }
        else {
            this.ignoreBeforeUnload = true;
            try {
                callback?.();
            }
            finally {
                this.ignoreBeforeUnload = false;
            }
        }
    }
    async shutdown() {
        this.logService.info('[lifecycle] shutdown triggered');
        this.beforeUnloadListener?.dispose();
        this.unloadListener?.dispose();
        await this.storageService.flush(WillSaveStateReason.SHUTDOWN);
        this.doShutdown();
    }
    doShutdown(vetoShutdown) {
        const logService = this.logService;
        this.storageService.flush(WillSaveStateReason.SHUTDOWN);
        let veto = false;
        function handleVeto(vetoResult, id) {
            if (typeof vetoShutdown !== 'function') {
                return;
            }
            if (vetoResult instanceof Promise) {
                logService.error(`[lifecycle] Long running operations before shutdown are unsupported in the web (id: ${id})`);
                veto = true;
            }
            if (vetoResult === true) {
                logService.info(`[lifecycle]: Unload was prevented (id: ${id})`);
                veto = true;
            }
        }
        this._onBeforeShutdown.fire({
            reason: 2 ,
            veto(value, id) {
                handleVeto(value, id);
            },
            finalVeto(valueFn, id) {
                handleVeto(valueFn(), id);
            }
        });
        if (veto && typeof vetoShutdown === 'function') {
            return vetoShutdown();
        }
        return this.onUnload();
    }
    onUnload() {
        if (this.didUnload) {
            return;
        }
        this.didUnload = true;
        this._register(addDisposableListener(mainWindow, EventType.PAGE_SHOW, (e) => this.onLoadAfterUnload(e)));
        const logService = this.logService;
        this._onWillShutdown.fire({
            reason: 2 ,
            joiners: () => [],
            token: CancellationToken.None,
            join(promise, joiner) {
                logService.error(`[lifecycle] Long running operations during shutdown are unsupported in the web (id: ${joiner.id})`);
            },
            force: () => { },
        });
        this._onDidShutdown.fire();
    }
    onLoadAfterUnload(event) {
        const wasRestoredFromCache = event.persisted;
        if (!wasRestoredFromCache) {
            return;
        }
        this.withExpectedShutdown({ disableShutdownHandling: true }, () => mainWindow.location.reload());
    }
    doResolveStartupKind() {
        let startupKind = super.doResolveStartupKind();
        if (typeof startupKind !== 'number') {
            const timing = firstOrDefault(performance.getEntriesByType('navigation'));
            if (timing?.type === 'reload') {
                startupKind = 3 ;
            }
        }
        return startupKind;
    }
};
BrowserLifecycleService = ( (__decorate([
    ( (__param(0, ILogService))),
    ( (__param(1, IStorageService)))
], BrowserLifecycleService)));

export { BrowserLifecycleService };
